import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowRight,
    Building2,
    ClipboardList,
    GraduationCap,
    MapPinned,
    ShieldCheck,
    Users,
    Wrench
} from 'lucide-react';
import Logo from './Logo';
import Footer from './Footer';
import './LandingPage.css';
import './PropertyManagementPage.css';

const WORKFLOWS = [
    {
        label: 'SB 553 · Workplace violence prevention',
        title: 'Plan, session, and incident records',
        body: 'Organize a workplace violence prevention plan template, employer-entered training-session details, incident records, and supported exports.',
        icon: ShieldCheck,
        tone: 'blue'
    },
    {
        label: 'Gov. Code §12950.1 · Harassment training',
        title: 'Assignments and administrative evidence',
        body: 'Create employer-selected assignments, open official California training resources, and maintain supported employer-review history.',
        icon: GraduationCap,
        tone: 'violet'
    }
];

const OPERATION_SIGNALS = [
    {
        title: 'Multiple properties or offices',
        body: 'A shared roster can reduce duplicate administrative lists while the two record workflows remain separate.',
        icon: MapPinned
    },
    {
        title: 'Resident and public contact',
        body: 'Property, maintenance, leasing, and office teams can create workplace records that need a consistent home.',
        icon: Users
    },
    {
        title: 'Operational work across teams',
        body: 'Plans, sessions, incidents, assignments, and files often live with different people and in different folders.',
        icon: Wrench
    }
];

const SUPPORTS = [
    'A shared employee roster for supported workflows',
    'A structured workplace violence prevention plan template',
    'SB 553 workplace-violence training-session records',
    'Structured workplace violence incident records',
    'Harassment-training assignments and official CRD resource links',
    'Supported record-index and PDF exports'
];

const founderEmail = 'mailto:support@compcleared.com?subject=Property%20management%20workflow%20conversation&body=Hi%20CompCleared%2C%0A%0AI%20would%20like%20to%20discuss%20how%20our%20property-management%20team%20currently%20organizes%20workplace%20records.%0A';

function PropertyManagementPage() {
    const navigate = useNavigate();

    return (
        <div className="landing-page property-page">
            <nav className="landing-nav" aria-label="Primary navigation">
                <div className="nav-content">
                    <button className="brand-home" onClick={() => navigate('/')} aria-label="CompCleared home">
                        <Logo size="small" />
                    </button>
                    <div className="nav-buttons">
                        <button className="btn-secondary" onClick={() => navigate('/pricing')}>Pricing</button>
                        <button className="btn-primary" onClick={() => navigate('/login')}>Log In</button>
                    </div>
                </div>
            </nav>

            <main>
                <section className="property-hero" aria-labelledby="property-hero-title">
                    <div className="property-hero-grid" aria-hidden="true" />
                    <div className="property-hero-inner">
                        <div className="property-hero-copy">
                            <span className="property-eyebrow">
                                <Building2 size={17} aria-hidden="true" />
                                For California property-management teams
                            </span>
                            <h1 id="property-hero-title">
                                Keep workplace records from becoming another <span>property emergency.</span>
                            </h1>
                            <p>
                                Property teams already coordinate residents, owners, vendors, maintenance, leasing,
                                and offices. CompCleared gives the supported workplace-violence and harassment-training
                                administrative records a clearer home—without treating the two requirements as one.
                            </p>
                            <div className="property-hero-actions">
                                <a className="btn-property-primary" href={founderEmail}>
                                    Talk with the founder <ArrowRight size={18} aria-hidden="true" />
                                </a>
                                <button className="btn-property-secondary" onClick={() => navigate('/pricing')}>
                                    See the $149 annual plan
                                </button>
                            </div>
                            <p className="property-hero-note">
                                Founding-customer onboarding included · No legal-pressure pitch · Not legal advice
                            </p>
                        </div>

                        <div className="property-workspace-preview" aria-label="Separate CompCleared record workflows">
                            <div className="preview-topline">
                                <span>CompCleared workspace</span>
                                <span className="preview-status"><span aria-hidden="true" /> Organized separately</span>
                            </div>
                            <div className="preview-roster">
                                <Users size={20} aria-hidden="true" />
                                <div>
                                    <strong>Shared employee roster</strong>
                                    <span>Employer-entered roles and work locations</span>
                                </div>
                            </div>
                            <div className="preview-workflows">
                                <div className="preview-workflow preview-workflow-blue">
                                    <ShieldCheck size={22} aria-hidden="true" />
                                    <span>SB 553 records</span>
                                    <small>Plan · Sessions · Incidents</small>
                                </div>
                                <div className="preview-divider" aria-hidden="true">≠</div>
                                <div className="preview-workflow preview-workflow-violet">
                                    <GraduationCap size={22} aria-hidden="true" />
                                    <span>Harassment training</span>
                                    <small>Assignments · Review history</small>
                                </div>
                            </div>
                            <p>One workspace does not mean one requirement.</p>
                        </div>
                    </div>
                </section>

                <section className="property-signals" aria-labelledby="property-signals-title">
                    <div className="section-shell">
                        <div className="section-heading compact-heading">
                            <span className="section-kicker">Built around operating reality</span>
                            <h2 id="property-signals-title">Property management creates distributed paperwork.</h2>
                            <p>
                                CompCleared is designed for the owner, operations manager, office manager, or HR
                                generalist who needs supported records to be findable across an active business.
                            </p>
                        </div>
                        <div className="property-signal-grid">
                            {OPERATION_SIGNALS.map(({ title, body, icon: Icon }) => (
                                <article className="property-signal-card" key={title}>
                                    <Icon size={24} aria-hidden="true" />
                                    <h3>{title}</h3>
                                    <p>{body}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="property-separate" aria-labelledby="property-separate-title">
                    <div className="section-shell">
                        <div className="section-heading section-heading-dark">
                            <span className="section-kicker">Separate by design</span>
                            <h2 id="property-separate-title">Two California workflows. One practical roster.</h2>
                            <p>
                                Each workflow keeps its own purpose and record types. CompCleared does not assume one
                                training, plan, certificate, or document satisfies both.
                            </p>
                        </div>
                        <div className="property-workflow-grid">
                            {WORKFLOWS.map(({ label, title, body, icon: Icon, tone }) => (
                                <article className={`property-workflow-card property-workflow-${tone}`} key={label}>
                                    <div className="property-workflow-icon"><Icon size={26} aria-hidden="true" /></div>
                                    <span>{label}</span>
                                    <h3>{title}</h3>
                                    <p>{body}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="property-supports" aria-labelledby="property-supports-title">
                    <div className="section-shell property-supports-layout">
                        <div className="property-supports-copy">
                            <span className="section-kicker">What the workspace supports</span>
                            <h2 id="property-supports-title">Start with the records your team actually maintains.</h2>
                            <p>
                                CompCleared organizes employer-entered information and supported exports. It does not
                                determine whether a law covers your company, authenticate uploaded documents, provide
                                training content, or decide whether your records are legally sufficient.
                            </p>
                            <button className="btn-property-inline" onClick={() => navigate('/pricing')}>
                                Review pricing and features <ArrowRight size={17} aria-hidden="true" />
                            </button>
                        </div>
                        <ul className="property-support-list" aria-label="Supported CompCleared property-management records">
                            {SUPPORTS.map((item) => (
                                <li key={item}>
                                    <ClipboardList size={19} aria-hidden="true" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section className="property-closing" aria-labelledby="property-closing-title">
                    <div className="property-closing-card">
                        <span className="section-kicker section-kicker-light">Founding-customer conversation</span>
                        <h2 id="property-closing-title">Show us how your records work today.</h2>
                        <p>
                            Start with a 15-minute conversation. If CompCleared fits the workflow, the annual plan is
                            $149 and includes a 45-minute founder-led setup session.
                        </p>
                        <a className="btn-property-primary" href={founderEmail}>
                            Email the founder <ArrowRight size={18} aria-hidden="true" />
                        </a>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}

export default PropertyManagementPage;
